import { useCCF } from '@app/ccf/company-report/ccf-context';
import { reportSlugs } from '@app/ccf/company-report/slugs';
import PreviewSheet from '@app/components/preview/preview-sheet';
import PreviewSheetSection from '@app/components/preview/preview-sheet-chart-section';
import PreviewSheetTableSection from '@app/components/preview/preview-sheet-table-section';
import { useGetSupplyChainLogisticsPreview } from '@shared/api/hooks/ccf/activities/supply-chain-logistics';
import SimplePieChart from '@shared/components/chart/simple-pie-chart';
import { ChartConfig, ChartLegend } from '@shared/components/ui/chart';
import { formatNumber } from '@shared/lib/utils';
import { useState } from 'react';

export default function SupplyChainLogisticsPreviewSheet() {
  const [isOpen, setIsOpen] = useState(false);
  const { uuid: reportUuid, categories } = useCCF();

  const category = categories?.find(
    (item) => item.slug === reportSlugs.supplyChainLogistics
  );

  const { data: supplyChainLogisticsData, status } =
    useGetSupplyChainLogisticsPreview({
      companyReportUuid: reportUuid!,
      params: {},
      enabled: isOpen,
    });

  const supplyTotalEmissionsTonnes =
    supplyChainLogisticsData?.supplyTotalEmissionsTonnes;

  const supplyTotalWeight =
    supplyChainLogisticsData?.supplyTotalWeightKiloTonnes;

  const distributionTotalEmissions =
    supplyChainLogisticsData?.distributionTotalEmissionsTonnes;

  const distributionTotalWeight =
    supplyChainLogisticsData?.distributionTotalWeightKiloTonnes;

  const highestEmissionsActivities =
    supplyChainLogisticsData?.emissionsByActivity.map((activity) => {
      return {
        name: activity.activity,
        emissionsTonnes: activity.totalEmissionsTonnes,
      };
    }) || [];

  const emissionsBySource =
    supplyChainLogisticsData?.emissionsBySource.map((source, idx) => {
      return {
        source: source.source,
        emissions: source.totalEmissionsTonnes,
        fill: `var(--chart-${idx + 2})`,
      };
    }) || [];

  const chartConfig = {
    'Your suppliers': {
      label: 'Your suppliers',
      color: 'var(--chart-2)',
    },
    'Outsourced providers': {
      label: 'Outsourced providers',
      color: 'var(--chart-3)',
    },
    'Your clients and downstream': {
      label: 'Your clients and downstream',
      color: 'var(--chart-4)',
    },
  } satisfies ChartConfig;
  return (
    <PreviewSheet
      status={status}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      className="w-[1000px]"
      style={{ maxWidth: '28rem' }}
      metricsTitle={['Supply of purchased goods', 'Distribution of products']}
      metrics={[
        {
          title: 'Total emissions',
          value: formatNumber(supplyTotalEmissionsTonnes!),
          unit: 'tCO2e',
        },
        {
          title: 'Total emissions',
          value: formatNumber(distributionTotalEmissions!),
          unit: 'tCO2e',
        },
        {
          title: 'Weight transported',
          value: formatNumber(supplyTotalWeight!),
          unit: 'kt',
          valueClassName: 'text-primary-500',
          titleClassName: 'text-primary-500',
        },
        {
          title: 'Weight transported',
          value: formatNumber(distributionTotalWeight!),
          unit: 'kt',
          valueClassName: 'text-primary-500',
          titleClassName: 'text-primary-500',
        },
      ]}
      title={`Scope ${category?.scope} - ${category?.name}`}
    >
      <div className="flex w-full items-center space-y-2 overflow-hidden">
        <SimplePieChart
          nameKey="source"
          dataKey="emissions"
          chartData={emissionsBySource!.reverse()}
          chartConfig={chartConfig}
          innerRadius={45}
          className="z-10 h-40 w-48"
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
        />
        <div className="flex h-full flex-col justify-center gap-y-2">
          {Object.entries(chartConfig).map((item, idx) => {
            return (
              <div key={idx}>
                <ChartLegend name={item[1].label} color={item[1].color} />
              </div>
            );
          })}
        </div>
      </div>

      <PreviewSheetSection title="Highest emissions activities">
        <PreviewSheetTableSection
          columnName="Activity"
          items={highestEmissionsActivities!}
        />
      </PreviewSheetSection>
    </PreviewSheet>
  );
}
